import React, { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { Info, Users, CheckCircle, Eye } from 'lucide-react';
import api from '../services/api';
import { toast } from '../components/Toast';
import logger from '../utils/logger';

const ProjectWorkspacePermissions = () => {
  const { projectId, isOwner } = useOutletContext();
  const navigate = useNavigate();
  const [collaborators, setCollaborators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    if (!isOwner) {
      navigate(`/projects/${projectId}/workspace`);
      return;
    }
    loadCollaborators();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, isOwner]);

  const loadCollaborators = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/projects/${projectId}/workspace/permissions`);
      setCollaborators(res.data || []);
    } catch (err) {
      logger.error('Erreur chargement collaborateurs:', err);
    } finally {
      setLoading(false);
    }
  };

  const togglePermission = async (userId, currentValue) => {
    setSaving(userId);
    try {
      await api.put(`/projects/${projectId}/workspace/permissions/${userId}`, {
        can_manage: !currentValue
      });
      
      // Mettre à jour localement
      setCollaborators(prev => 
        prev.map(c => c.user_id === userId ? { ...c, can_manage: !currentValue } : c)
      );
    } catch (err) {
      logger.error('Erreur mise à jour permission:', err);
      toast.error('Erreur lors de la mise à jour de la permission');
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl md:text-2xl font-bold text-gray-800">
          Gestion des accès
        </h1>
        <p className="text-gray-500 text-sm">
          Autorisez les collaborateurs à gérer les finances du projet
        </p>
      </div>

      {/* Info box */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-medium text-blue-800">À propos des permissions</h4>
            <p className="text-sm text-blue-700">
              Par défaut, tous les collaborateurs peuvent <strong>consulter</strong> les finances du projet.
              Activez l'option "Peut gérer" pour permettre à un collaborateur d'<strong>ajouter, modifier et supprimer</strong> des données financières.
            </p>
          </div>
        </div>
      </div>

      {/* Liste des collaborateurs */}
      {collaborators.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-8 text-center">
          <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Aucun collaborateur</h3>
          <p className="text-gray-500">
            Ce projet n'a pas encore de collaborateurs. Ajoutez des membres à votre projet pour leur donner accès aux finances.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <div className="divide-y">
            {collaborators.map((collab) => (
              <div key={collab.user_id} className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white font-bold">
                    {collab.avatar ? (
                      <img src={collab.avatar} alt="" className="w-12 h-12 rounded-full object-cover" />
                    ) : (
                      collab.name?.charAt(0).toUpperCase()
                    )}
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-800">{collab.name}</h4>
                    <p className="text-sm text-gray-500">{collab.email}</p>
                    <span className="inline-block mt-1 px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-600">
                      {collab.role || 'Collaborateur'}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <span className={`text-sm font-medium flex items-center gap-1 ${collab.can_manage ? 'text-green-600' : 'text-gray-500'}`}>
                      {collab.can_manage ? <><CheckCircle className="w-4 h-4" /> Peut gérer</> : <><Eye className="w-4 h-4" /> Lecture seule</>}
                    </span>
                  </div>

                  <button
                    onClick={() => togglePermission(collab.user_id, collab.can_manage)}
                    disabled={saving === collab.user_id}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      collab.can_manage ? 'bg-purple-600' : 'bg-gray-300'
                    } ${saving === collab.user_id ? 'opacity-50' : ''}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        collab.can_manage ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                    {saving === collab.user_id && (
                      <span className="absolute inset-0 flex items-center justify-center">
                        <span className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full"></span>
                      </span>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gray-50 rounded-xl p-4">
          <h4 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
            <Eye className="w-5 h-5" /> Lecture seule
          </h4>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• Voir le tableau de bord</li>
            <li>• Consulter les encaissements</li>
            <li>• Consulter les dépenses</li>
            <li>• Consulter les factures</li>
            <li>• Voir le bilan</li>
          </ul>
        </div>

        <div className="bg-purple-50 rounded-xl p-4">
          <h4 className="font-medium text-purple-800 mb-2 flex items-center gap-2">
            <CheckCircle className="w-5 h-5" /> Peut gérer
          </h4>
          <ul className="text-sm text-purple-700 space-y-1">
            <li>• Tout ce qui est en lecture seule</li>
            <li>• Ajouter des encaissements</li>
            <li>• Ajouter des dépenses</li>
            <li>• Créer des factures</li>
            <li>• Gérer les produits et clients</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ProjectWorkspacePermissions;
